import React, { useState } from "react";
import {
  Chart as ChartJS,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import StatsApi from "~/admin/apis/stats.api";
import PageDataLoader from "~/admin/components/PageDataLoader";
import useDataLoading from "~/admin/hooks/useDataLoading";
import useLayout from "~/hooks/useLayout";
import useMount from "~/hooks/useMount";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function StatsPage() {
  const { setLayout } = useLayout();
  const dataLoading = useDataLoading();
  const [stats, setStats] = useState(null);

  const [influencersStatusChartData, setInfluencersStatusChartData] =
    useState(null);
  const [companiesStatusChartData, setCompaniesStatusChartData] =
    useState(null);
  const [socialNetworksChartData, setSocialNetworksChartData] = useState(null);
  const [categoriesChartData, setCategoriesChartData] = useState(null);
  const [contractTypeChartData, setContractTypeChartData] = useState(null);
  const [paymentTypeChartData, setPaymentTypeChartData] = useState(null);
  const [postTypeChartData, setPostTypeChartData] = useState(null);

  useMount(() => {
    setLayout({
      title: "Statistiques",
      activeMenuKeys: ["stats"],
    });
    loadData();
  });

  const loadData = async () => {
    dataLoading.start();
    setStats(null);

    let error = null;
    const stats = await StatsApi.get().catch((e) => {
      error = e;
      return null;
    });

    dataLoading.stop(error);

    if (stats) {
      setStats(stats);

      setInfluencersStatusChartData({
        labels: ["Valide", "Invalide", "Non vérifié"],
        datasets: [
          {
            data: [
              stats.influencers.valid,
              stats.influencers.invalid,
              stats.influencers.toCheck,
            ],
            backgroundColor: ["#0ab39c", "#f06548", "#f7b84b"],
            borderWidth: 1,
          },
        ],
      });

      setCompaniesStatusChartData({
        labels: ["Valide", "Invalide", "Non vérifié"],
        datasets: [
          {
            data: [
              stats.companies.valid,
              stats.companies.invalid,
              stats.companies.toCheck,
            ],
            backgroundColor: ["#0ab39c", "#f06548", "#f7b84b"],
            borderWidth: 1,
          },
        ],
      });

      setSocialNetworksChartData({
        labels: stats.socialNetworks.map((socialNetwork) => socialNetwork.name),
        datasets: [
          {
            data: stats.socialNetworks.map(
              (socialNetwork) => socialNetwork.influencers
            ),
            backgroundColor: ["#FFF"],
          },
        ],
      });

      setCategoriesChartData({
        labels: stats.categories.map((category) => category.name),
        datasets: [
          {
            label: "Influenceurs",
            data: stats.categories.map((category) => category.influencers),
            backgroundColor: ["#FFF"],
          },
          {
            label: "Entreprises",
            data: stats.categories.map((category) => category.companies),
            backgroundColor: ["#4819f2"],
          },
        ],
      });

      setContractTypeChartData({
        labels: stats.contractTypes.map((ct) => ct.name),
        datasets: [
          {
            label: "Influenceurs",
            data: stats.contractTypes.map((ct) => ct.influencers),
            backgroundColor: ["#FFF"],
          },
          {
            label: "Entreprises",
            data: stats.contractTypes.map((ct) => ct.companies),
            backgroundColor: ["#4819f2"],
          },
        ],
      });

      setPaymentTypeChartData({
        labels: stats.paymentTypes.map((pt) => pt.name),
        datasets: [
          {
            label: "Influenceurs",
            data: stats.paymentTypes.map((pt) => pt.influencers),
            backgroundColor: ["#FFF"],
          },
          {
            label: "Entreprises",
            data: stats.paymentTypes.map((pt) => pt.companies),
            backgroundColor: ["#4819f2"],
          },
        ],
      });

      setPostTypeChartData({
        labels: stats.postTypes.map((pt) => pt.name),
        datasets: [
          {
            label: "Influenceurs",
            data: stats.postTypes.map((pt) => pt.influencers),
            backgroundColor: ["#FFF"],
          },
          {
            label: "Entreprises",
            data: stats.postTypes.map((pt) => pt.companies),
            backgroundColor: ["#4819f2"],
          },
        ],
      });
    }
  };

  return (
    <PageDataLoader isLoading={dataLoading.isLoading}>
      <>
        <div className="w-full h-[60px] border-b-2 border-slate-700 flex justify-end items-center px-10">
          <div className="font-bold text-2xl">Statistiques</div>
        </div>
        <div className="p-6 flex flex-col gap-y-6 overflow-auto">
          <div className="flex justify-center gap-x-8">
            <div className="p-8 bg-slate-800 rounded-xl flex flex-col gap-y-6 items-center">
              <div className="text-2xl font-bold text-center">
                Utilisateurs :{" "}
                <span className="italic font-semibold text-xl">
                  {stats?.users.nbTotal}
                </span>
                <div className="text-sm">
                  Premium :{" "}
                  <span>
                    {stats?.users.nbPremium} (
                    {Math.round(
                      (stats?.users.nbPremium / stats?.users.nbTotal) * 100
                    )}
                    %)
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xl underline font-semibold mb-2">
                  Nouveaux Utilisateurs
                </div>
                <div className="text-md">
                  Cette dernière semaine :{" "}
                  <span>{stats?.users.newThisWeek} </span>
                  {stats?.users.newLastWeek !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.users.newThisWeek / stats?.users.newLastWeek) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
                <div className="text-md">
                  Ce dernier mois : <span>{stats?.users.newThisMonth} </span>
                  {stats?.users.newLastMonth !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.users.newThisMonth /
                          stats?.users.newLastMonth) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
                <div className="mt-4">Nombre de match : {stats?.nbMatch}</div>
                <div className="mt-4">
                  Nombre de conversation commencée après un match :{" "}
                  {stats?.nbMessageAfterMatch}
                </div>
              </div>
            </div>
            <div className="p-8 bg-slate-800 rounded-xl flex flex-col gap-y-8 items-center">
              <div className="text-2xl font-bold text-center">
                Influenceurs :{" "}
                <span className="italic font-semibold text-xl">
                  {stats?.influencers.nbTotal}
                </span>
                <br></br>
                <div className="text-sm">
                  Premium :{" "}
                  <span>
                    {stats?.influencers.nbPremium} (
                    {Math.round(
                      (stats?.influencers.nbPremium /
                        stats?.influencers.nbTotal) *
                        100
                    )}
                    %)
                  </span>
                </div>
              </div>
              <div className="h-52 w-52 m-auto">
                <Doughnut
                  data={influencersStatusChartData}
                  options={{
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          color: "white",
                        },
                      },
                    },
                  }}
                />
              </div>
              <div>
                <div className="text-xl underline font-semibold mb-2">
                  Nouveaux influenceurs
                </div>
                <div className="text-md">
                  Cette dernière semaine :{" "}
                  <span>{stats?.influencers.newThisWeek} </span>
                  {stats?.influencers.newLastWeek !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.influencers.newThisWeek /
                          stats?.influencers.newLastWeek) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
                <div className="text-md">
                  Ce dernier mois :{" "}
                  <span>{stats?.influencers.newThisMonth} </span>
                  {stats?.influencers.newLastMonth !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.influencers.newThisMonth /
                          stats?.influencers.newLastMonth) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-8 bg-slate-800 rounded-xl flex flex-col gap-y-8 items-center">
              <div className="text-2xl font-bold text-center">
                Entreprises :{" "}
                <span className="italic font-semibold text-xl">
                  {stats?.companies.nbTotal}
                </span>
                <div className="text-sm">
                  Premium :{" "}
                  <span>
                    {stats?.companies.nbPremium} (
                    {Math.round(
                      (stats?.companies.nbPremium / stats?.companies.nbTotal) *
                        100
                    )}
                    %)
                  </span>
                </div>
              </div>
              <div className="h-52 w-52 m-auto">
                <Doughnut
                  data={companiesStatusChartData}
                  options={{
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          color: "white",
                        },
                      },
                    },
                  }}
                />
              </div>
              <div>
                <div className="text-xl underline font-semibold mb-2">
                  Nouvelles entreprises
                </div>
                <div className="text-md">
                  Cette dernière semaine :{" "}
                  <span>{stats?.companies.newThisWeek} </span>
                  {stats?.companies.newLastWeek !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.companies.newThisWeek /
                          stats?.companies.newLastWeek) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
                <div className="text-md">
                  Ce dernier mois :{" "}
                  <span>{stats?.companies.newThisMonth} </span>
                  {stats?.companies.newLastMonth !== 0 ? (
                    <span className="text-success text-xs">
                      (+
                      {Math.round(
                        (stats?.companies.newThisMonth /
                          stats?.companies.newLastMonth) *
                          100
                      )}
                      %)
                    </span>
                  ) : (
                    <span className="text-success text-xs">{"(+100%)"}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full justify-around space-x-20 p-8 flex-wrap bg-slate-800 rounded-xl">
            <div className="flex-1 max-w-[500px]">
              <Bar
                data={socialNetworksChartData}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                    x: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                  },
                  plugins: {
                    legend: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: "Influenceurs par réseaux social",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
            <div className="flex-1 max-w-[500px]">
              <Bar
                data={categoriesChartData}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                    x: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                  },
                  plugins: {
                    legend: {
                      labels: {
                        color: "white", // Définir la couleur des labels ici
                      },
                    },
                    title: {
                      display: true,
                      text: "Utilisateurs par catégorie",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
          </div>
          <div className="flex w-full justify-around space-x-20 p-8 flex-wrap bg-slate-800 rounded-xl">
            <div className="flex-1 max-w-[500px]">
              <Bar
                data={contractTypeChartData}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                    x: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                  },
                  plugins: {
                    legend: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: "Utilisateurs par type de contrat",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
            <div className="flex-1 max-w-[500px]">
              <Bar
                data={paymentTypeChartData}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                    x: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                  },
                  plugins: {
                    legend: {
                      labels: {
                        color: "white", // Définir la couleur des labels ici
                      },
                    },
                    title: {
                      display: true,
                      text: "Utilisateurs par type de payement",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
            <div className="flex-1 max-w-[500px]">
              <Bar
                data={postTypeChartData}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                    x: {
                      ticks: { color: "white", beginAtZero: true },
                    },
                  },
                  plugins: {
                    legend: {
                      labels: {
                        color: "white", // Définir la couleur des labels ici
                      },
                    },
                    title: {
                      display: true,
                      text: "Utilisateurs par type de post",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </>
    </PageDataLoader>
  );
}
